import type { ScenarioConfig } from "../types/domain";
import { parkingAmountLabel, parkingPaymentInfo } from "./parkingPayment";

export const scenarioConfigs: ScenarioConfig[] = [
  {
    id: "parcel",
    title: "快递助手",
    shortTitle: "快递",
    icon: "package",
    themeRole: "accent",
    providerLabel: "支付宝小程序服务",
    threshold: 72,
    rules: [
      { field: "dayType", operator: "equals", value: "weekday", points: 12, reason: "工作日通勤路径更稳定" },
      { field: "timeOfDay", operator: "equals", value: "commuteHome", points: 24, reason: "已进入下班回家时段" },
      { field: "location", operator: "equals", value: "office", points: 10, reason: "车辆准备从办公区出发" },
      { field: "parcelPending", operator: "truthy", points: 28, reason: "2 件待取包裹" },
      { field: "parcelOnRoute", operator: "truthy", points: 18, reason: "包裹已绑定家附近驿站" },
      { field: "parked", operator: "notEquals", value: true, points: 8, reason: "车辆可直接规划取件路线" }
    ],
    primaryAction: "查看详情",
    successMessage: "已进入家附近驿站导航，快递详情保留可查看。",
    detail: {
      headline: "家附近驿站有待取包裹",
      description: "当前快递已绑定家附近驿站，左侧可直接导航去取，右侧保留快递详情。",
      metrics: [
        { label: "取件点", value: "家附近驿站" },
        { label: "绕行", value: "0.4km" },
        { label: "预计到达", value: "18:36" }
      ],
      alipayAssetId: "alipay-parcel"
    }
  },
  {
    id: "carwash",
    title: "洗车管家",
    shortTitle: "洗车",
    icon: "sparkles",
    themeRole: "success",
    providerLabel: "支付宝小程序服务",
    threshold: 72,
    rules: [
      { field: "dayType", operator: "equals", value: "weekend", points: 14, reason: "周末行程更适合顺路洗车" },
      { field: "weather", operator: "notEquals", value: "rainy", points: 10, reason: "当前天气适合洗车" },
      { field: "vehicleDirty", operator: "gte", value: 66, points: 22, reason: "车身脏污度较高" },
      { field: "carwashCoupon", operator: "truthy", points: 18, reason: "附近门店有可用优惠" },
      { field: "destinationDistanceKm", operator: "gte", value: 12, points: 10, reason: "后续路线旁有可顺路服务点" },
      { field: "forecastGoodDays", operator: "gte", value: 3, points: 16, reason: "未来几天天气较好" }
    ],
    primaryAction: "顺路洗车",
    successMessage: "已把洗车目的地加入路线。",
    detail: {
      headline: "顺路洗车点可用",
      description: "未来几天天气较好，车身脏污度较高，建议顺路洗车。",
      metrics: [
        { label: "到店价", value: "29.9 元" },
        { label: "绕路", value: "4 分钟" },
        { label: "天气", value: "未来较好" }
      ],
      alipayAssetId: "alipay-carwash"
    }
  },
  {
    id: "charging",
    title: "充电管家",
    shortTitle: "充电",
    icon: "battery",
    themeRole: "warning",
    providerLabel: "支付宝小程序服务",
    threshold: 68,
    rules: [
      { field: "batteryLevel", operator: "lte", value: 28, points: 26, reason: "当前电量偏低" },
      { field: "rangeKm", operator: "lte", value: 160, points: 18, reason: "剩余续航接近后续行程下限" },
      { field: "chargerAvailability", operator: "gte", value: 4, points: 16, reason: "支付宝发现目的地附近快充桩可用" },
      { field: "chargerPrice", operator: "lte", value: 1.2, points: 12, reason: "当前充电价格合适" },
      { field: "destinationDistanceKm", operator: "gte", value: 10, points: 10, reason: "前往目的地仍有一段距离" }
    ],
    primaryAction: "目的地补能",
    successMessage: "已为你找到目的地附近的快充站。",
    detail: {
      headline: "目的地补能",
      description: "目的地附近有可用快充站，电价1.2元/度，停车10元/小时。",
      metrics: [
        { label: "目的地", value: "已设置目的地" },
        { label: "附近充电站", value: "目的地附近快充站" },
        { label: "电价", value: "1.2 元/度" },
        { label: "停车", value: "10 元/小时" }
      ],
      alipayAssetId: "alipay-charging"
    }
  },
  {
    id: "parking",
    title: "停车助手",
    shortTitle: "停车",
    icon: "parking",
    themeRole: "danger",
    providerLabel: "支付宝小程序服务",
    threshold: 66,
    rules: [
      { field: "parkingQrDetected", operator: "truthy", points: 38, reason: "扫描到车外停车缴费二维码" },
      { field: "parked", operator: "truthy", points: 18, reason: "车辆已进入停车缴费场景" },
      { field: "location", operator: "equals", value: "destination", points: 10, reason: "当前位置与停车场场景匹配" },
      { field: "parkingDistanceM", operator: "lte", value: 450, points: 8, reason: "停车场步行距离可接受" }
    ],
    primaryAction: "去支付",
    successMessage: "已完成支付宝停车缴费。",
    detail: {
      headline: "停车费用待支付",
      description: `${parkingPaymentInfo.plateNumber} 在${parkingPaymentInfo.lotName}已停${parkingPaymentInfo.duration}，待支付${parkingAmountLabel()}。`,
      metrics: [
        { label: "停车场", value: "极氪中心 B2" },
        { label: "停留", value: parkingPaymentInfo.duration },
        { label: "待支付", value: parkingAmountLabel() },
        { label: "来源", value: "支付宝" }
      ],
      alipayAssetId: "alipay-parking"
    }
  },
  {
    id: "refuel",
    title: "加油助手",
    shortTitle: "加油",
    icon: "fuel",
    themeRole: "warning",
    providerLabel: "支付宝小程序服务",
    threshold: 66,
    rules: [
      { field: "fuelLevel", operator: "lte", value: 30, points: 26, reason: "当前油量偏低" },
      { field: "fuelRangeKm", operator: "lte", value: 220, points: 20, reason: "燃油续航接近后续行程下限" },
      { field: "destinationDistanceKm", operator: "gte", value: 24, points: 12, reason: "后续行程仍有距离" },
      { field: "traffic", operator: "notEquals", value: "heavy", points: 10, reason: "前方加油站绕路时间可接受" }
    ],
    primaryAction: "导航加油",
    successMessage: "已进入顺路加油站导航。",
    detail: {
      headline: "顺路加油站可用",
      description: "结合当前燃油续航和后续目的地距离，建议在前方站点补充适量油量。",
      metrics: [
        { label: "站点", value: "中石化滨江站" },
        { label: "油号", value: "92#" },
        { label: "来源", value: "支付宝" }
      ],
      alipayAssetId: "alipay-refuel"
    }
  },
  {
    id: "collision",
    title: "碰撞回溯",
    shortTitle: "回溯",
    icon: "history",
    themeRole: "danger",
    providerLabel: "安全数据服务",
    threshold: 100,
    rules: [
      { field: "scriptTrigger", operator: "equals", value: "collision-review", points: 100, reason: "演示脚本已触发碰撞回溯" }
    ],
    primaryAction: "查看回溯",
    successMessage: "已生成碰撞前后关键数据回溯。",
    detail: {
      headline: "碰撞回溯已生成",
      description: "已整理碰撞前后速度、制动、转向和影像关键片段，可用于事故复盘。",
      metrics: [
        { label: "时间窗", value: "前30秒 / 后10秒" },
        { label: "数据源", value: "车身域 + 智驾域" },
        { label: "状态", value: "已加密归档" }
      ],
      alipayAssetId: "alipay-refuel"
    }
  }
];
